/**
 * Audit → taking the trail elsewhere (docs/audit-export.md): the chain's Export (a period, for
 * checking or as CSV, ending with the newest link and a count) and Forwarding, where an
 * administrator sends every audit row to the organisation's security tools (Splunk, Microsoft
 * Sentinel or a signed webhook) as it is written, and sees how delivery stands. Both stand on the
 * Audit view; nothing here has a route of its own.
 *
 * Forwarding is a list group (Layout.tsx): how delivery stands with Send a test event at its end,
 * the destination as a choice, its fields, then one Save in the last row.
 */

import { time } from '@aioe/shared/wording/dateTime';
import type { AuditExportFormat, AuditForwardingPut, AuditForwardingTestResult, AuditForwardingView, AuditForwardKind } from '@aioe/shared';
import { ArrowDownToLine } from '@gravity-ui/icons';
import { Popover } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';

import { ApiError, auditForwardingRoutes, download, problemText } from '../api/client.ts';
import { Button, ChoiceSelect, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, StatusMark, Tick, type ArtTone } from '../components/ui.tsx';
import { FormField } from '../components/ui/FormSheet.tsx';
import { timeAgo, useLoad } from '../hooks.ts';

/** A local date (YYYY-MM-DD) as the instant its day starts; `next` for the day after (an inclusive "to"). */
const dayStart = (d: string, next = false) => {
  const t = new Date(`${d}T00:00:00`);
  if (next) t.setDate(t.getDate() + 1);
  return t.toISOString();
};

/** The Export button on the chain: a period and a format in a small popover. */
export function AuditExportButton() {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [format, setFormat] = useState<AuditExportFormat>('ndjson');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const url = auditForwardingRoutes.exportUrl(format, from ? dayStart(from) : undefined, to ? dayStart(to, true) : undefined);
      await download(url, `audit-chain-${from || 'start'}-to-${to || today}.${format}`);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 409 ? 'An export is already running for this organisation. Try again when it finishes.' : problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Popover>
      <Button size="sm" variant="secondary">
        <ArrowDownToLine /> Export
      </Button>
      <Popover.Content placement="bottom end" className="w-80 max-w-[calc(100vw-1rem)]">
        <Popover.Dialog className="grid gap-3 p-3" aria-label="Export the chain">
          <div className="grid grid-cols-2 gap-2">
            <FormField label="From">
              <input className={inputClass} type="date" value={from} max={to || today} onChange={(e) => setFrom(e.target.value)} />
            </FormField>
            <FormField label="To">
              <input className={inputClass} type="date" value={to} min={from || undefined} max={today} onChange={(e) => setTo(e.target.value)} />
            </FormField>
          </div>
          <Segment aria-label="Format" size="sm" selectedKey={format} onSelectionChange={(k) => setFormat(k === 'csv' ? 'csv' : 'ndjson')} className="w-fit">
            <Segment.Item id="ndjson">For checking</Segment.Item>
            <Segment.Item id="csv">CSV</Segment.Item>
          </Segment>
          <p className="text-xs text-muted">Leave both dates empty for the whole chain. The file ends with the newest link and a count, so it can be checked offline.</p>
          <ErrorNote error={error} />
          <Button size="sm" variant="primary" isPending={busy} onPress={() => void save()}>
            Download
          </Button>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

const KINDS: Record<AuditForwardKind, string> = {
  off: 'Off',
  'splunk-hec': 'Splunk (HTTP Event Collector)',
  sentinel: 'Microsoft Sentinel',
  webhook: 'Webhook',
};

const STATES: Record<AuditForwardingView['state'], { mark: 'connected' | 'working' | 'error' | 'idle'; label: string }> = {
  delivering: { mark: 'connected', label: 'Delivering' },
  behind: { mark: 'working', label: 'Catching up' },
  failing: { mark: 'error', label: 'Failing' },
  off: { mark: 'idle', label: 'Off' },
};

type Draft = Record<string, string>;

const draftOf = (v: AuditForwardingView | undefined): Draft => ({
  ...(v?.destination ?? {}),
});
/** The address a destination is known by: a change of it is a new destination, which needs its secret again. */
const addressOf = (kind: AuditForwardKind, d: Draft) => (kind === 'sentinel' ? [d.endpoint, d.ruleId, d.stream, d.directoryId, d.clientId].join('|') : (d.url ?? ''));

function Field({ label, value, onChange, placeholder, secret, mono = true, wide }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; secret?: boolean; mono?: boolean; wide?: boolean }) {
  return (
    <FormField label={label} className={wide ? 'sm:col-span-2' : ''}>
      <input className={`${inputClass} ${mono && !secret ? 'mono' : ''}`} type={secret ? 'password' : 'text'} autoComplete={secret ? 'new-password' : 'off'} spellCheck={false} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </FormField>
  );
}

/** Forwarding: where the trail goes, its secret (never shown again), Save and a test event, and the standing in words. */
export function AuditForwardingSection({ tone: _tone }: { tone: ArtTone }) {
  // Operators read the trail but do not set where it goes: the section stays away for them.
  const view = useLoad(() => auditForwardingRoutes.view().catch((e: unknown) => (e instanceof ApiError && e.status === 403 ? null : Promise.reject(e))), [], 30_000);
  const v = view.data ?? undefined;
  const [kind, setKind] = useState<AuditForwardKind>('off');
  const [draft, setDraft] = useState<Draft>({});
  const [secret, setSecret] = useState('');
  const [beginning, setBeginning] = useState(false);
  const [busy, setBusy] = useState<'save' | 'test'>();
  const [error, setError] = useState<string>();
  const [tested, setTested] = useState<AuditForwardingTestResult>();
  // A saved change (or the first answer) resets the form to what is stored: adjusted while rendering, not in an effect.
  const stamp = v ? `${v.kind}/${v.updatedAt ?? ''}` : '';
  const [seen, setSeen] = useState('');
  if (v && stamp !== seen) {
    setSeen(stamp);
    setKind(v.kind);
    setDraft(draftOf(v));
    setSecret('');
    setBeginning(false);
  }
  if (view.data === null) return null;

  const set = (k: string) => (value: string) => setDraft((d) => ({ ...d, [k]: value }));
  const stored = !!v && v.kind === kind && addressOf(kind, draft) === addressOf(v.kind, draftOf(v));
  const keepsSecret = stored && v!.hasSecret;
  const need = (...keys: string[]) => keys.every((k) => draft[k]?.trim());
  const complete =
    kind === 'off' ||
    ((keepsSecret || secret.trim().length > 0) && (kind === 'splunk-hec' ? need('url') : kind === 'sentinel' ? need('endpoint', 'ruleId', 'directoryId', 'clientId') : need('url') && (keepsSecret || secret.trim().length >= 16)));
  const changed =
    !v ||
    kind !== v.kind ||
    JSON.stringify(
      Object.entries(draft)
        .filter(([, x]) => x.trim())
        .sort(),
    ) !== JSON.stringify(Object.entries(draftOf(v)).sort()) ||
    secret.length > 0;
  const secretHint = keepsSecret ? 'Kept: type to replace it' : undefined;
  const opt = (k: string) => (draft[k]?.trim() ? { [k]: draft[k]!.trim() } : {});

  const body = (): AuditForwardingPut => {
    const start = stored ? {} : { start: beginning ? ('beginning' as const) : ('now' as const) };
    const s = secret.trim();
    if (kind === 'splunk-hec') return { kind, url: draft.url!.trim(), ...(s ? { token: s } : {}), ...opt('index'), ...opt('sourcetype'), ...start };
    if (kind === 'sentinel') return { kind, endpoint: draft.endpoint!.trim(), ruleId: draft.ruleId!.trim(), ...opt('stream'), directoryId: draft.directoryId!.trim(), clientId: draft.clientId!.trim(), ...(s ? { clientSecret: s } : {}), ...start };
    if (kind === 'webhook') return { kind, url: draft.url!.trim(), ...(s ? { secret: s } : {}), ...start };
    return { kind: 'off' };
  };
  const save = async () => {
    setBusy('save');
    setError(undefined);
    setTested(undefined);
    try {
      await auditForwardingRoutes.set(body());
      view.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  const test = async () => {
    setBusy('test');
    setError(undefined);
    try {
      setTested(await auditForwardingRoutes.test());
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  const s = v ? STATES[v.state] : undefined;

  return (
    <ListGroup title="Forwarding" description="Every audit row, sent to your security tools as it is written. Checked every minute.">
      {view.error ? (
        <ListBlock>
          <ErrorNote error={view.error} />
        </ListBlock>
      ) : null}
      {!v && !view.error ? <ListBlock className="text-sm text-muted">Reading the destination…</ListBlock> : null}
      {v && s ? (
        <ListRow
          title={v.kind === 'off' ? 'Delivery' : KINDS[v.kind]}
          description={
            <>
              <span className="block">{v.summary}</span>
              {v.kind !== 'off' ? (
                <span className="block tabular-nums">
                  {v.delivered.toLocaleString()} {v.delivered === 1 ? 'row' : 'rows'} delivered · {v.backlog.toLocaleString()} waiting
                  {v.lastDeliveredAt ? ` · last ${timeAgo(v.lastDeliveredAt)}` : ''}
                  {v.nextAttemptAt ? ` · next try at ${time(v.nextAttemptAt, { seconds: true })}` : ''}
                </span>
              ) : null}
              {tested ? (
                <span className="mt-1 block" role="status">
                  <StatusMark wrap state={tested.ok ? 'connected' : 'error'} label={`Test event: ${tested.message}`} />
                </span>
              ) : null}
            </>
          }
          status={<StatusMark state={s.mark} label={v.kind === 'off' ? 'Off' : v.state === 'delivering' && !v.lastDeliveredAt ? 'Set up' : s.label} />}
          control={
            v.kind !== 'off' ? (
              <Button size="sm" variant="secondary" isPending={busy === 'test'} isDisabled={!!busy} onPress={() => void test()}>
                Send a test event
              </Button>
            ) : undefined
          }
        />
      ) : null}
      {v ? (
        <ListRow
          title="Destination"
          control={
            <ChoiceSelect
              label="Destination"
              className="w-64 max-w-full"
              value={kind}
              options={KINDS}
              onChange={(k) => {
                setKind(k);
                setDraft(k === v?.kind ? draftOf(v) : {});
                setSecret('');
              }}
            />
          }
        />
      ) : null}
      {v && kind !== 'off' ? (
        <ListBlock>
          <div className="grid gap-4 sm:grid-cols-2">
            {kind === 'splunk-hec' && (
              <>
                <Field wide label="Collector address" value={draft.url ?? ''} onChange={set('url')} placeholder="https://splunk.example.com:8088" />
                <Field secret label="HEC token" value={secret} onChange={setSecret} placeholder={secretHint} />
                <Field label="Index (optional)" value={draft.index ?? ''} onChange={set('index')} placeholder="main" />
                <Field label="Source type (optional)" value={draft.sourcetype ?? ''} onChange={set('sourcetype')} placeholder="aioe:audit" />
              </>
            )}
            {kind === 'sentinel' && (
              <>
                <Field wide label="Logs ingestion endpoint" value={draft.endpoint ?? ''} onChange={set('endpoint')} placeholder="https://mojoup-dce-xxxx.australiaeast-1.ingest.monitor.azure.com" />
                <Field label="Data collection rule immutable id" value={draft.ruleId ?? ''} onChange={set('ruleId')} placeholder="dcr-…" />
                <Field label="Stream (optional)" value={draft.stream ?? ''} onChange={set('stream')} placeholder="Custom-MojoUpSecurityEvents" />
                <Field label="Directory (tenant) id" value={draft.directoryId ?? ''} onChange={set('directoryId')} placeholder="00000000-0000-0000-0000-000000000000" />
                <Field label="Application (client) id" value={draft.clientId ?? ''} onChange={set('clientId')} />
                <Field secret label="Client secret" value={secret} onChange={setSecret} placeholder={secretHint} />
                <p className="text-xs text-muted sm:col-span-2">Register an application in your Microsoft Entra tenant and give it the Monitoring Metrics Publisher role on the rule. Rows arrive in the MojoUpSecurityEvents_CL shape.</p>
              </>
            )}
            {kind === 'webhook' && (
              <>
                <Field wide label="Address" value={draft.url ?? ''} onChange={set('url')} placeholder="https://siem.example.com/aioe" />
                <Field secret label="Signing secret" value={secret} onChange={setSecret} placeholder={secretHint ?? 'At least 16 characters'} />
                <p className="self-end text-xs text-muted">Each delivery is signed: X-Aioe-Signature is sha256= and the HMAC-SHA256 of the timestamp, a dot and the body.</p>
              </>
            )}
          </div>
          {!stored ? (
            <div className="mt-4">
              <Tick checked={beginning} onChange={setBeginning}>
                Send the whole trail first, not only rows from now on
              </Tick>
            </div>
          ) : null}
        </ListBlock>
      ) : null}
      {v && (kind !== 'off' || v.kind !== 'off' || error) ? (
        <ListBlock className="grid gap-2">
          <ErrorNote error={error} />
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" variant="primary" isPending={busy === 'save'} isDisabled={!!busy || !changed || !complete} onPress={() => void save()}>
              {kind === 'off' && v.kind !== 'off' ? 'Stop forwarding' : 'Save'}
            </Button>
            {kind !== 'off' ? <span className="text-xs text-muted">The secret is sealed and never shown again.</span> : null}
          </div>
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
